import { useEffect } from 'react';

import { SearchUtils, Skeleton } from '@spotify/confidence-core-react';
import { useObservations } from '@spotify/plugin-confidence-issues-react';
import {
  RecordingsCodeBlock,
  type RecordingsCodeBlockProps,
} from '@spotify/plugin-confidence-recordings';

import { getRecordingReference } from './evidenceView';
import { useEvidenceIssueName } from './useEvidenceIssueName';

export const IssueRecordingsCodeBlock = ({
  references,
}: RecordingsCodeBlockProps) => {
  const issueName = useEvidenceIssueName();
  const bareReferences = references.filter(
    reference =>
      reference.findingId === undefined &&
      reference.startTime === undefined &&
      reference.endTime === undefined,
  );
  const { observations, isLoading, hasNextPage, nextPage, error } =
    useObservations({
      filter: `issue:${SearchUtils.escapeQuery(issueName ?? '')} AND resource:(${bareReferences.map(reference => SearchUtils.escapeQuery(reference.name)).join(' OR ')})`,
      pageSize: 100,
      skip: !issueName || bareReferences.length === 0,
    });

  useEffect(() => {
    if (hasNextPage && !isLoading && !error) void nextPage();
  }, [hasNextPage, isLoading, nextPage, error]);

  if (isLoading || (hasNextPage && !error)) {
    return <Skeleton variant="rect" width="100%" height={160} />;
  }

  const findingsByRecording = new Map<string, Set<string>>();
  for (const observation of observations ?? []) {
    const reference = getRecordingReference(observation);
    if (!reference?.findingId) continue;
    const findings =
      findingsByRecording.get(reference.name) ?? new Set<string>();
    findings.add(reference.findingId);
    findingsByRecording.set(reference.name, findings);
  }
  const resolvedReferences = references.flatMap(reference => {
    if (!bareReferences.includes(reference)) return [reference];
    const findings = findingsByRecording.get(reference.name);
    return findings?.size
      ? Array.from(findings, findingId => ({ ...reference, findingId }))
      : [reference];
  });

  return <RecordingsCodeBlock references={resolvedReferences} />;
};
